import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, formatBytes, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { statusClass } from "@/pages/Dashboard";
import UploadPanel from "@/components/event/UploadPanel";
import OrganiserPanel from "@/components/event/OrganiserPanel";
import StylePanel from "@/components/event/StylePanel";
import ReelBuilderPanel from "@/components/event/ReelBuilderPanel";
import ReviewSharePanel from "@/components/event/ReviewSharePanel";
import { ArrowLeft, Film, Loader2, Upload, LayoutList, Palette, Clapperboard, Share2, HardDrive } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { v: "upload", label: "Add Photos & Videos", icon: Upload },
  { v: "organise", label: "Arrange Highlights", icon: LayoutList },
  { v: "style", label: "Choose a Style", icon: Palette },
  { v: "build", label: "Create Reel", icon: Clapperboard },
  { v: "review", label: "Review & Share", icon: Share2 },
];

export default function EventDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [event, setEvent] = useState(null);
  const [tab, setTab] = useState("upload");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/events/${id}`);
      setEvent(data);
    } catch (e) {
      toast.error(apiError(e));
      nav("/");
    } finally {
      setLoading(false);
    }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  if (loading || !event)
    return <div className="min-h-screen flex items-center justify-center bg-navy-deep"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-navy-deep grain">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-navy-deep/80 border-b border-navy-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Button data-testid="back-to-dashboard" variant="ghost" size="icon" onClick={() => nav("/")} className="text-muted-foreground hover:text-ivory shrink-0">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <h1 className="font-display text-2xl sm:text-3xl text-ivory truncate">{event.name}</h1>
                  <Badge variant="outline" className={`shrink-0 ${statusClass(event.status)}`}>{event.status}</Badge>
                </div>
                <p className="text-sm text-muted-foreground truncate">
                  {event.client_name || "—"} · {formatDate(event.event_date)} · {event.event_type}
                </p>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-2 text-sm text-muted-foreground shrink-0">
              <HardDrive className="w-4 h-4 text-gold" /> {formatBytes(event.storage_used)}
              <span className="mx-1">·</span> {event.asset_count} assets
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-navy-card border border-navy-border h-auto flex-wrap p-1.5 gap-1 mb-6">
            {TABS.map((t) => (
              <TabsTrigger key={t.v} value={t.v} data-testid={`tab-${t.v}`}
                className="data-[state=active]:bg-gold data-[state=active]:text-navy text-muted-foreground rounded-lg px-4 py-2.5 gap-2">
                <t.icon className="w-4 h-4" /> <span className="hidden sm:inline">{t.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="upload"><UploadPanel event={event} reload={load} goNext={() => setTab("organise")} /></TabsContent>
          <TabsContent value="organise"><OrganiserPanel event={event} goNext={() => setTab("style")} /></TabsContent>
          <TabsContent value="style"><StylePanel event={event} reload={load} goNext={() => setTab("build")} /></TabsContent>
          <TabsContent value="build"><ReelBuilderPanel event={event} reload={load} goNext={() => setTab("review")} /></TabsContent>
          <TabsContent value="review"><ReviewSharePanel event={event} reload={load} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
